import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments';
import { Observable } from 'rxjs';
import { DocumentBatchEntity } from '../../entities/document-batch.entity';
import { ResponseApiDto } from '../../DTOs/_response-api.dto';

/** Pacote de PDFs em lote: pedido, acompanhamento e download do ZIP. */
@Injectable({
    providedIn: 'root',
})
export class DocumentBatchApi {
    private prefix = 'document/batch';
    private _httpClient = inject(HttpClient);

    /**
     * Pede o pacote. Responde de imediato com o lote já criado (todos os itens na fila):
     * quem gera é o engine, e quem acompanha é o canal `BATCH-<id>`.
     */
    public create(
        orderId: string,
        documentIds: string[]
    ): Observable<ResponseApiDto<DocumentBatchEntity>> {
        const url = `${environment.API_BASE}${this.prefix}`;

        return this._httpClient.post<ResponseApiDto<DocumentBatchEntity>>(url, {
            orderId,
            documentIds,
        });
    }

    public getById(id: string): Observable<ResponseApiDto<DocumentBatchEntity>> {
        const url = `${environment.API_BASE}${this.prefix}/${id}`;

        return this._httpClient.get<ResponseApiDto<DocumentBatchEntity>>(url);
    }

    /** Pacote que a tela do serviço deve mostrar ao abrir (rodando ou o último válido). */
    public getCurrentByOrder(
        orderId: string
    ): Observable<ResponseApiDto<DocumentBatchEntity | null>> {
        const url = `${environment.API_BASE}${this.prefix}/order/${orderId}`;

        return this._httpClient.get<ResponseApiDto<DocumentBatchEntity | null>>(url);
    }

    /** URL assinada do ZIP, válida por minutos — o arquivo é privado no storage. */
    public getDownloadUrl(id: string): Observable<ResponseApiDto<{ url: string }>> {
        const url = `${environment.API_BASE}${this.prefix}/${id}/download`;

        return this._httpClient.get<ResponseApiDto<{ url: string }>>(url);
    }

    /** Tira um documento do pacote: o ZIP sai sem ele, sem esperar o watchdog. */
    public skipItem(
        id: string,
        documentId: string
    ): Observable<ResponseApiDto<DocumentBatchEntity>> {
        const url = `${environment.API_BASE}${this.prefix}/${id}/item/${documentId}/skip`;

        return this._httpClient.patch<ResponseApiDto<DocumentBatchEntity>>(url, null);
    }

    /** Devolve à fila um documento que falhou. Reabre o pacote se ele já tinha fechado. */
    public retryItem(
        id: string,
        documentId: string
    ): Observable<ResponseApiDto<DocumentBatchEntity>> {
        const url = `${environment.API_BASE}${this.prefix}/${id}/item/${documentId}/retry`;

        return this._httpClient.patch<ResponseApiDto<DocumentBatchEntity>>(url, null);
    }

    public cancel(id: string): Observable<ResponseApiDto<void>> {
        const url = `${environment.API_BASE}${this.prefix}/${id}/cancel`;

        return this._httpClient.patch<ResponseApiDto<void>>(url, null);
    }
}
